<template>
  <div @drop="onDropFile">
    <v-snackbar
      bottom>
      {{ $tc('message.upload.drophere', 1) }}
      <v-btn flat>
        <v-icon color="accent">file_upload</v-icon>
      </v-btn>
    </v-snackbar>
    <v-text-field
      @input="onInputTextValue"
      @click:append="$refs.fileInput.click()"
      :readonly="!uploadingImages || loading"
      :hint="computedHint"
      :value="computedSelectedFileName"
      :label="computedLabel"
      :loading="loading"
      :error-messages="errorMessages"
      append-icon="file_upload"
      persistent-hint
    >
      <template v-slot:append-outer>
        <v-tooltip top class="text-xs-center">
          <v-icon v-show="showResizeTool" slot="activator" @click="openResizeTool">crop</v-icon>
          <span>{{$t('message.clipping.selector.crop')}}</span>
        </v-tooltip>
      </template>
    </v-text-field>
    <input style="display: none" type="file" :accept="extensions" ref="fileInput" @change="onFileChange"/>
    <nx-file-upload-preview v-if="selectedFile" class="mt-2" :file="selectedFile" />

    <nx-clipping-tool-cropper
      v-if="selectedFileIsImage || forceClipTemporalImage"
      :active="cropTool"
      :aspect-ratios="beforeUploadClippingsConfig"
      :src="selectedFileBlob"
      @crop="acceptResize"
      @dismiss="dismissResizeTool"
      ref="cropper"
      :title="$t('message.clipping.clipper.resize')"
    ></nx-clipping-tool-cropper>
  </div>
</template>

<script>
import NxFileUploadPreview from '@/modules/upload/FileUploadPreview';
import NxClippingToolCropper from '@/modules/clipping/components/Cropper';
import { validImageExtensions } from '@/modules/file/config';
import api from './api';
import { cropToAspectRatio } from '@/modules/upload/utils';
import { mapGetters } from 'vuex';

export default {
  name: 'nx-file-uploader',
  components: { NxClippingToolCropper, NxFileUploadPreview },
  props: {
    label: {
      type: String,
    },
    disabled: {
      type: Boolean,
    },
    extensions: {
      type: String,
    },
    selectedFileName: {
      type: String,
    },
    selectedFile: {
      type: File,
    },
    hint: {
      type: String,
    },
    beforeUploadClippingsConfig: {
      type: Array,
      default: () => ([]),
    },
    beforeUploadForceClip: {
      type: Boolean,
      default: false,
    },
    beforeUploadAutomaticClip: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      originalImageFile: null,
      cropTool: false,
      previewLoaded: false,
      forceClipTemporalImage: null,
      errorMessages: [],
      loading: false,
    };
  },
  computed: {
    ...mapGetters(['availableClippingAspectRatios']),
    uploadingImages() {
      return this.extensions && this.extensions.includes('image');
    },
    selectedFileBlob() {
      if (this.originalImageFile) {
        return URL.createObjectURL(this.originalImageFile);
      }
      if (!this.selectedFile) {
        return null;
      }
      return URL.createObjectURL(this.selectedFile);
    },
    selectedFileType() {
      if (!this.selectedFile) {
        return null;
      }
      return this.selectedFile.type;
    },
    selectedFileIsImage() {
      return this.selectedFileType && this.selectedFileType.startsWith('image');
    },
    computedHint() {
      if (this.selectedFile) {
        return null;
      }
      if (this.hint) {
        return this.hint;
      }
      if (this.uploadingImages) {
        return this.$t('message.upload.selectImage');
      }
      return this.$t('message.upload.chooseFile');
    },
    computedSelectedFileName() {
      if (this.selectedFileName) {
        return this.selectedFileName;
      }
      if (this.selectedFile) {
        return this.selectedFile.name;
      }
      return undefined;
    },
    showResizeTool() {
      return this.selectedFileIsImage && this.beforeUploadClippingsConfig.length > 0;
    },
    computedLabel() {
      if (this.label) {
        return this.label;
      }
      return this.$t('message.upload.chooseFile');
    },
  },
  methods: {
    acceptResize(crop, canvas) {
      // Transform the canvas to a blob
      const selectedFile = this.forceClipTemporalImage || this.selectedFile;
      canvas.toBlob((blob) => {
        // Transform the blob to a file
        const file = new File([blob], selectedFile.name, {
          type: blob.type,
        });

        // Configure the file as the new file to upload
        this.$emit('file', file);
        this.closeResizeTool();
      }, selectedFile.type, 0.8);
    },
    dismissResizeTool() {
      if (this.forceClipTemporalImage) {
        this.forceClipTemporalImage = null;
        this.clear();
      }
      this.closeResizeTool();
    },
    openResizeTool() {
      this.cropTool = true;
    },
    closeResizeTool() {
      this.$refs.cropper.reset();
      this.cropTool = false;
    },
    async onFileChange(evt) {
      const [file] = evt.currentTarget.files;
      if (!file) {
        return;
      }

      if (!this.uploadingImages) {
        this.$emit('file', file);
        return;
      }

      // Save original clip reference
      this.originalImageFile = new File([file], file.name, { type: file.type });
      // Automatic crop
      const automaticCropRatio = this.availableClippingAspectRatios[this.beforeUploadClippingsConfig[0]];
      if (this.beforeUploadAutomaticClip && automaticCropRatio) {
        const crop = await cropToAspectRatio(file, automaticCropRatio.width / automaticCropRatio.height);
        this.$emit('file', crop);
        return;
      }
      // Force manual crop
      if (this.beforeUploadForceClip) {
        this.forceClipTemporalImage = file;
        this.openResizeTool();
        return;
      }

      this.$emit('file', file);
    },
    onDropFile(evt) {
      evt.preventDefault();
      const [file] = evt.dataTransfer.files;
      if (file) this.$emit('file', file);
    },
    async createFileFromURL(url) {
      const blob = await api.getImageBlob(url);
      const pathname = new URL(url).pathname || url;
      return new File([blob], pathname.substring(pathname.lastIndexOf('/') + 1), { type: 'image/jpeg' });
    },
    async onInputTextValue(value) {
      if (!this.uploadingImages) {
        // Nothing to do here if we are not uploading an image
        return;
      }
      try {
        const parsedURL = new URL(value);
        if (!validImageExtensions.some(extension => parsedURL.pathname.endsWith(extension))) {
          // Not a valid image extension
          this.clear();
          this.errorMessages.push(this.$t('message.upload.error.unsupportedFile'));
          return;
        }
        this.loading = true;
        const file = await this.createFileFromURL(value);
        this.$emit('file', file);
        this.textValue = value;
      } catch (e) {
        this.clear();
        this.errorMessages.push(this.$t('message.upload.error.invalidURL'));
      } finally {
        this.loading = false;
      }
    },
    // Limpia los ficheros seleccionados
    // este método se invoca desde el componente
    // padre que lo encapsula
    // por ejemplo: en WizardVideo
    // this.$refs.uploader.clear();
    clear() {
      this.$refs.fileInput.value = '';
      this.errorMessages = [];
    },
  },
};
</script>
